import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

// Compare's seat map: Skybox base SVG + transparent clickable overlay, with
// zoom/pan and a toolbar above it. Memoized so hover and pan frames re-render
// only the map, never the whole Compare page. Optional hoverText(sectionId)
// → string | null replaces the readout's price / "no listings" tag (Reports);
// keep it stable (useCallback) so the memo holds.

const IDENTITY = { scale: 1, tx: 0, ty: 0 };
const MIN_SCALE = 1;
const MAX_SCALE = 8;
const WHEEL_COMMIT_MS = 120;
// Sections with no listings, like Skybox's own map
const EMPTY_FILL = '#e9e9ee';
// Ids go into a <style> element: anything outside this is skipped, not escaped
const SAFE_ID = /^[A-Za-z0-9_-]+$/;

// Keep the zoomed map inside its viewport: with transform-origin 0 0, content
// spans w*scale, so the offsets live in [w*(1-scale), 0] (idem for height)
const clampMapZoom = (z, w, h) => ({
  scale: z.scale,
  tx: Math.min(0, Math.max(w * (1 - z.scale), z.tx)),
  ty: Math.min(0, Math.max(h * (1 - z.scale), z.ty)),
});

// Zoom by `factor` keeping the viewport point (cx, cy) fixed under the cursor
const zoomAbout = (z, factor, cx, cy, w, h) => {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, z.scale * factor));
  if (scale === z.scale) return z;
  const ratio = scale / z.scale;
  return clampMapZoom({ scale, tx: cx - (cx - z.tx) * ratio, ty: cy - (cy - z.ty) * ratio }, w, h);
};

const toTransform = (z) => `translate(${z.tx}px, ${z.ty}px) scale(${z.scale})`;

function CompareSeatMap({ svgContent, chartData, selectedSections, onToggleSection, availableSectionIds, hoverText }) {
  const [hoveredId, setHoveredId] = useState(null);
  // Committed zoom (drives the rendered style, cursor class and Reset button).
  // zoomRef is the live value: during a drag or a wheel burst it leads the
  // state and is written straight to the DOM, then committed once.
  const [zoom, setZoom] = useState(IDENTITY);
  const zoomRef = useRef(IDENTITY);
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const rafRef = useRef(0);
  const commitTimerRef = useRef(null);
  const dragRef = useRef({ active: false, moved: false });

  // One pending frame at most: later moves just update zoomRef
  const setLiveZoom = useCallback((z) => {
    zoomRef.current = z;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (contentRef.current) contentRef.current.style.transform = toTransform(zoomRef.current);
    });
  }, []);

  // Write the DOM too: React skips the style when the committed value equals
  // what it last rendered, even if a live frame has moved the node since
  const commitZoom = useCallback(() => {
    clearTimeout(commitTimerRef.current);
    commitTimerRef.current = null;
    if (contentRef.current) contentRef.current.style.transform = toTransform(zoomRef.current);
    setZoom(zoomRef.current);
  }, []);

  // A different venue chart makes the old transform meaningless
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    clearTimeout(commitTimerRef.current);
    dragRef.current = { active: false, moved: false };
    zoomRef.current = IDENTITY;
    commitZoom();
    setHoveredId(null);
  }, [chartData, commitZoom]);

  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(commitTimerRef.current);
  }, []);

  const zoomBy = (factor) => {
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    zoomRef.current = zoomAbout(zoomRef.current, factor, rect.width / 2, rect.height / 2, rect.width, rect.height);
    commitZoom();
  };

  const resetZoom = () => {
    zoomRef.current = IDENTITY;
    commitZoom();
  };

  // Native wheel listener (React's synthetic onWheel can't preventDefault, so
  // the page would scroll while zooming). Bound via CALLBACK REF, not an
  // effect, so it attaches whenever the container node actually mounts.
  // Proportional factor: a mouse notch and a trackpad's stream of tiny deltas
  // both zoom smoothly (pinch arrives as ctrlKey wheel and works the same).
  const handleWheel = useCallback((e) => {
    const el = viewportRef.current;
    if (!el) return;
    e.preventDefault();
    const drag = dragRef.current;
    if (drag.active && drag.moved) return; // the pan owns the transform
    const rect = el.getBoundingClientRect();
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1;
    const factor = Math.min(2, Math.max(0.5, Math.exp(-e.deltaY * unit * 0.0015)));
    const next = zoomAbout(zoomRef.current, factor, e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
    if (next === zoomRef.current) return;
    // Pressed but not yet panning: pan from the new transform
    if (drag.active) { drag.baseTx = next.tx; drag.baseTy = next.ty; }
    setLiveZoom(next);
    clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(commitZoom, WHEEL_COMMIT_MS);
  }, [setLiveZoom, commitZoom]);

  const setViewportRef = useCallback((el) => {
    const prev = viewportRef.current;
    if (prev && prev !== el) prev.removeEventListener('wheel', handleWheel);
    viewportRef.current = el;
    if (el) el.addEventListener('wheel', handleWheel, { passive: false });
  }, [handleWheel]);

  const handlePointerDown = (e) => {
    const z = zoomRef.current;
    if (z.scale <= 1) return;
    // No pointer capture on the bare press: capture retargets the click to the
    // container, so the section paths would never see it and click-to-select
    // died while zoomed. We capture only once a real drag starts (see move).
    // The zoom buttons live in the toolbar, outside this container.
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      active: true, moved: false, captured: false, pointerId: e.pointerId,
      startX: e.clientX, startY: e.clientY, baseTx: z.tx, baseTy: z.ty,
      w: rect.width, h: rect.height,
    };
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag.active || e.pointerId !== drag.pointerId) return;
    // Released outside the map before a pan captured the pointer: no
    // pointerup reached us, so end here instead of panning on plain hover
    if (e.pointerType === 'mouse' && e.buttons === 0) { endDrag(e); return; }
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) > 5) {
      drag.moved = true;
      // Real pan: now capture so tracking survives the pointer leaving the map
      try { e.currentTarget.setPointerCapture(drag.pointerId); drag.captured = true; } catch (_) { /* unsupported pointer */ }
      setHoveredId(null);
    }
    if (!drag.moved) return;
    setLiveZoom(clampMapZoom({ scale: zoomRef.current.scale, tx: drag.baseTx + dx, ty: drag.baseTy + dy }, drag.w, drag.h));
  };

  const endDrag = (e) => {
    const drag = dragRef.current;
    if (!drag.active || e.pointerId !== drag.pointerId) return;
    drag.active = false;
    if (drag.moved) commitZoom();
    // Clear after the click this pointerup produces: a real pan suppresses
    // section-select once, the next clean click works normally
    setTimeout(() => { dragRef.current.moved = false; }, 0);
  };

  const handleClickCapture = (e) => {
    if (dragRef.current.moved) e.stopPropagation();
  };

  // CRITICAL: force the base SVG onto the chart's viewBox so overlay paths
  // (defined in chart coordinates) align with it
  const baseSvgHtml = useMemo(() => {
    if (!svgContent) return { __html: '' };
    let processed = svgContent;
    processed = processed.replace(/<svg([^>]*)width="[^"]*"/, '<svg$1width="100%"');
    processed = processed.replace(/<svg([^>]*)height="[^"]*"/, '<svg$1height="100%"');
    if (chartData?.width && chartData?.height) {
      const chartViewBox = `0 0 ${chartData.width} ${chartData.height}`;
      processed = processed.includes('viewBox')
        ? processed.replace(/viewBox\s*=\s*["'][^"']*["']/i, `viewBox="${chartViewBox}"`)
        : processed.replace(/<svg/, `<svg viewBox="${chartViewBox}"`);
    }
    if (!processed.includes('preserveAspectRatio')) {
      processed = processed.replace(/<svg/, '<svg preserveAspectRatio="xMidYMid meet"');
    }
    return { __html: processed };
  }, [svgContent, chartData]);

  const selectedIds = useMemo(() => new Set(selectedSections.map(s => s.id)), [selectedSections]);

  const overlayPaths = useMemo(() => {
    if (!chartData?.elements) return null;
    const { elements, sections = {}, groups = {} } = chartData;
    return elements.map((el, index) => {
      if (el.type !== 'path' || !el.id) return null;
      const sectionId = el.id;
      const isSelected = selectedIds.has(sectionId);
      const isHovered = hoveredId === sectionId;
      let fill = 'transparent';
      let stroke = 'transparent';
      let strokeWidth = 0;
      if (isSelected) {
        fill = 'rgba(240, 165, 0, 0.5)';
        stroke = '#f0a500';
        strokeWidth = 3;
      } else if (isHovered) {
        fill = 'rgba(255, 255, 255, 0.2)';
        stroke = '#fff';
        strokeWidth = 2;
      }
      return (
        <path
          key={index}
          d={el.path}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          data-section-id={sectionId}
          style={{ cursor: 'pointer', transition: 'fill 0.15s, stroke 0.15s' }}
          // Crossing sections mid-pan must not flash hover highlights
          onMouseEnter={() => { if (!dragRef.current.moved) setHoveredId(sectionId); }}
          onMouseLeave={() => { if (!dragRef.current.moved) setHoveredId(null); }}
          onClick={(e) => {
            e.stopPropagation();
            const info = sections[sectionId];
            onToggleSection({
              id: sectionId,
              name: info?.name || `Section ${sectionId}`,
              group: info ? groups[info.groupId] : null,
              lowestPrice: info?.lowestPrice,
              highestPrice: info?.highestPrice,
            });
          }}
        />
      );
    }).filter(Boolean);
  }, [chartData, selectedIds, hoveredId, onToggleSection]);

  // Grey out sections nobody is selling in. A sibling <style> keyed on the
  // base paths' data-sid avoids re-parsing the whole SVG string; an old cached
  // SVG without data-sid simply matches nothing.
  const emptySectionsCss = useMemo(() => {
    if (!availableSectionIds || !chartData?.sections) return '';
    const selectors = [];
    for (const id of Object.keys(chartData.sections)) {
      if (availableSectionIds.has(id) || !SAFE_ID.test(id)) continue;
      selectors.push(`.compare-map-section .base-layer path[data-sid="${id}"]`);
    }
    return selectors.length ? `${selectors.join(',\n')}{fill:${EMPTY_FILL}}` : '';
  }, [availableSectionIds, chartData]);

  const hoverInfo = hoveredId != null ? chartData?.sections?.[hoveredId] : null;
  const hoverEmpty = hoveredId != null && !!availableSectionIds && !availableSectionIds.has(String(hoveredId));
  // The tag after the section name: the caller's text when it gives
  // hoverText, else "no listings" over a greyed section or Skybox's price range
  let hoverTag = null;
  if (hoverText) {
    const note = hoveredId != null ? hoverText(String(hoveredId)) : null;
    if (note) hoverTag = <span className="hover-note">{note}</span>;
  } else if (hoverEmpty) {
    hoverTag = <span className="hover-empty">no listings</span>;
  } else if (hoverInfo?.lowestPrice > 0) {
    hoverTag = <span className="hover-price">${hoverInfo.lowestPrice} - ${hoverInfo.highestPrice}</span>;
  }

  if (!chartData) return null;

  return (
    <>
      {emptySectionsCss && <style>{emptySectionsCss}</style>}

      {/* Toolbar sits above the map so nothing covers its top row of sections */}
      <div className="map-toolbar">
        <div className="map-hover-readout">
          {hoveredId != null ? (
            <>
              <strong>{hoverInfo?.name || `Section ${hoveredId}`}</strong>
              {hoverInfo && chartData.groups?.[hoverInfo.groupId] && (
                <span className="hover-group">{chartData.groups[hoverInfo.groupId]}</span>
              )}
              {hoverTag}
            </>
          ) : (
            <span className="map-hover-placeholder">Hover a section</span>
          )}
        </div>
        <div className="map-zoom-buttons">
          <button type="button" title="Zoom in" onClick={() => zoomBy(1.5)}>+</button>
          <button type="button" title="Zoom out" onClick={() => zoomBy(1 / 1.5)}>&minus;</button>
          {zoom.scale > 1 && <button type="button" title="Reset zoom" onClick={resetZoom}>Reset</button>}
        </div>
      </div>

      {/* Layered map container - aspect ratio from chart dimensions */}
      <div
        ref={setViewportRef}
        className={`layered-map-container${zoom.scale > 1 ? ' map-zoomed' : ''}`}
        style={{ aspectRatio: `${chartData.width} / ${chartData.height}` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={handleClickCapture}
      >
        {/* One transform wrapper keeps base + overlay aligned while zoomed.
            The style comes from committed state; mid-gesture frames are
            written to the node directly and committed with the same value. */}
        <div
          ref={contentRef}
          className="map-zoom-content"
          style={{ transform: toTransform(zoom), transformOrigin: '0 0' }}
        >
          {/* Base layer: Skybox visual SVG */}
          <div className="base-layer" dangerouslySetInnerHTML={baseSvgHtml} />
          {/* Overlay layer: transparent interactive SVG in chart coordinates */}
          <div className="overlay-layer">
            <svg
              viewBox={`0 0 ${chartData.width} ${chartData.height}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'auto' }}
            >
              {overlayPaths}
            </svg>
          </div>
        </div>
      </div>
    </>
  );
}

export default React.memo(CompareSeatMap);
